import React, { useEffect, useState } from 'react'
import { Col, FormGroup, Row, Alert, Input } from 'reactstrap'
import { Controller, ErrorMessage, useFormContext } from 'react-hook-form'
import Select from 'react-select'
import Switch from 'react-switch'
import { useFormUtils } from 'views/shared/useFormUtils'
import { isNotAlpha, isNotNumericDecimal } from 'context/Utilities'
import PropTypes from 'prop-types'
import {
  plantYogurtAltTypeOptions,
  fatLevelOptions,
  flavorOptions,
} from './constants'

const PlantYogurtPackageReview = ({ item, isDisabled }) => {
  const [packaging, setPackaging] = useState({
    fkCategory: item.fkCategory,
    fkSubCategory: item.fkSubCategory,
    PlantYogurtAltType: item.PlantYogurtAltType,
    OtherPlantYogurtAltType: item.OtherPlantYogurtAltType,
    FatLevel: item.FatLevel,
    Flavor: item.Flavor,
    OtherFlavor: item.OtherFlavor,
    Size: item.Size,
    MultiPackageType: item.MultiPackageType,
    MultiPackageCount: item.MultiPackageCount,
  })

  const methods = useFormContext()
  const {
    setSelectReviewer,
    setSwitchReviewer,
    onChangeReviewer,
    asterisk,
  } = useFormUtils()

  useEffect(() => {
    if (item !== null) {
      const {
        PlantYogurtAltType,
        OtherPlantYogurtAltType,
        FatLevel,
        Flavor,
        OtherFlavor,
        Size,
        MultiPackageType,
        MultiPackageCount,
      } = item
      setPackaging({
        PlantYogurtAltType,
        OtherPlantYogurtAltType,
        FatLevel,
        Flavor,
        OtherFlavor,
        Size,
        MultiPackageType,
        MultiPackageCount,
      })
    }
  }, [item])

  return (
    <Row>
      <Col xs={6}>
        <p>
          <strong>Plant-Based Yogurt Alternative Type {asterisk}</strong>
        </p>
        <Controller
          as={Select}
          className="react-select primary"
          classNamePrefix="react-select"
          options={plantYogurtAltTypeOptions}
          defaultValue={{
            label: packaging.PlantYogurtAltType,
            value: packaging.PlantYogurtAltType,
          }}
          name="PlantYogurtAltType"
          control={methods.control}
          rules={{ required: true }}
          placeholder={'Select Plant-Based Yogurt Alternative Type'}
          onChange={([selected, value]) => {
            setSelectReviewer(item, selected, value)
            setPackaging({ ...packaging, PlantYogurtAltType: selected.value })
            return selected
          }}
          isDisabled={isDisabled}
        />
        <p className={'text-danger'}>
          <ErrorMessage
            errors={methods.errors}
            name="PlantYogurtAltType"
            message={'⚠ Please select a plant-based yogurt alternative type.'}
            as={<Alert color={'danger'} className={'mt-3'} />}
          />
        </p>
        {packaging.PlantYogurtAltType === 'Other' && (
          <FormGroup>
            <p>
              <strong>
                Other Plant-Based Yogurt Alternative Type (Please Specify){' '}
                {asterisk}
              </strong>
            </p>
            <Controller
              as={Input}
              name="OtherPlantYogurtAltType"
              defaultValue={packaging.OtherPlantYogurtAltType}
              placeholder="Other Plant-Based Yogurt Alternative Type"
              control={methods.control}
              onKeyDown={e => {
                if (isNotAlpha(e.key) && e.key !== ' ') {
                  e.preventDefault()
                }
              }}
              rules={{
                required: {
                  value: true,
                  message:
                    '⚠ Other plant-based yogurt alternative type is required',
                },
                maxLength: {
                  value: 15,
                  message:
                    '⚠ Other Plant-Based yogurt alternative type exceeds 15 characters.',
                },
              }}
              onChange={([event]) => {
                onChangeReviewer(item, event)
                return event
              }}
              disabled={isDisabled}
            />
            <ErrorMessage
              errors={methods.errors}
              name="OtherPlantYogurtAltType"
              as={<Alert color={'danger'} className={'mt-3'} />}
            />
          </FormGroup>
        )}

        <FormGroup>
          <p>
            <strong>Size {asterisk}</strong>
          </p>
          <Controller
            as={Input}
            name="Size"
            defaultValue={packaging.Size}
            control={methods.control}
            onKeyDown={e => isNotNumericDecimal(e.key) && e.preventDefault()}
            rules={{
              required: '⚠ Please enter product size',
              maxLength: { value: 5, message: '⚠ Size exceeds 5 characters' },
              pattern: {
                value: /^\d+(\.\d{1,2})?$/,
                message: '⚠ Maximum two decimal places allowed.',
              },
            }}
            onChange={([event]) => {
              onChangeReviewer(item, event)
              return event
            }}
            disabled={isDisabled}
          />
          <ErrorMessage
            errors={methods.errors}
            name="Size"
            as={<Alert color="danger" className="mt-3" />}
          />
        </FormGroup>
      </Col>

      <Col xs={6}>
        <FormGroup>
          <p>
            <strong>Fat Level {asterisk}</strong>
          </p>
          <Controller
            as={Select}
            className="react-select primary"
            classNamePrefix="react-select"
            options={fatLevelOptions}
            defaultValue={{
              label: packaging.FatLevel,
              value: packaging.FatLevel,
            }}
            name="FatLevel"
            control={methods.control}
            rules={{ required: true }}
            placeholder={'Select Fat Level'}
            onChange={([selected, value]) => {
              setSelectReviewer(item, selected, value)
              setPackaging({ ...packaging, FatLevel: selected.value })
              return selected
            }}
            isDisabled={isDisabled}
          />
          <ErrorMessage
            errors={methods.errors}
            name="FatLevel"
            message={'⚠ Please select a fat level.'}
            as={<Alert color={'danger'} className={'mt-3'} />}
          />
        </FormGroup>

        <FormGroup>
          <p>
            <strong>Flavor {asterisk}</strong>
          </p>
          <Controller
            as={Select}
            className="react-select primary"
            classNamePrefix="react-select"
            options={flavorOptions}
            defaultValue={{
              label: packaging.Flavor,
              value: packaging.Flavor,
            }}
            name="Flavor"
            control={methods.control}
            rules={{ required: true }}
            placeholder={'Select Flavor'}
            onChange={([selected, value]) => {
              setSelectReviewer(item, selected, value)
              setPackaging({ ...packaging, Flavor: selected.value })
              return selected
            }}
            isDisabled={isDisabled}
          />
          <ErrorMessage
            errors={methods.errors}
            name="Flavor"
            message={'⚠ Please select a flavor.'}
            as={<Alert color={'danger'} className={'mt-3'} />}
          />
        </FormGroup>

        {packaging.Flavor === 'Other' && (
          <FormGroup>
            <p>
              <strong>Other Flavor (Please Specify) {asterisk}</strong>
            </p>
            <Controller
              as={Input}
              name="OtherFlavor"
              defaultValue={packaging.OtherFlavor}
              placeholder={'Other Flavor'}
              control={methods.control}
              onKeyDown={e => {
                if (isNotAlpha(e.key) && e.key !== ' ') {
                  e.preventDefault()
                }
              }}
              rules={{
                required: {
                  value: true,
                  message: '⚠ Other flavor is required',
                },
                maxLength: {
                  value: 15,
                  message: '⚠ Other flavor exceeds 15 characters.',
                },
              }}
              onChange={([event]) => {
                onChangeReviewer(item, event)
                return event
              }}
              disabled={isDisabled}
            />
            <ErrorMessage
              errors={methods.errors}
              name="OtherFlavor"
              as={<Alert color={'danger'} className={'mt-3'} />}
            />
          </FormGroup>
        )}

        <FormGroup>
          <p>
            <strong>Multi-pack</strong>
          </p>
          <Controller
            as={Switch}
            name="MultiPackageType"
            id="MultiPackageType"
            control={methods.control}
            defaultValue={packaging.MultiPackageType}
            onChange={([checked, event, id]) => {
              setSwitchReviewer(item, checked, event, id)
              setPackaging({ ...packaging, MultiPackageType: checked })
              return checked
            }}
            disabled={isDisabled}
          />
        </FormGroup>

        {packaging.MultiPackageType === true && (
          <FormGroup>
            <p>
              <strong>Multi-pack Count {asterisk}</strong>
            </p>
            <Controller
              as={Input}
              name="MultiPackageCount"
              defaultValue={packaging.MultiPackageCount}
              placeholder={'Multi-pack Count'}
              control={methods.control}
              onKeyDown={e => {
                if (
                  isNaN(parseInt(e.key)) &&
                  ![
                    'Backspace',
                    'Delete',
                    'Tab',
                    'ArrowLeft',
                    'ArrowRight',
                  ].includes(e.key)
                ) {
                  e.preventDefault()
                }
              }}
              rules={{
                required: {
                  value: true,
                  message: '⚠ Multi pack count is required.',
                },
                maxLength: { value: 2, message: '⚠ 2 characters max.' },
              }}
              onChange={([event]) => {
                onChangeReviewer(item, event)
                return event
              }}
              disabled={isDisabled}
            />
            <ErrorMessage
              errors={methods.errors}
              name="MultiPackageCount"
              as={<Alert color={'danger'} className={'mt-3'} />}
            />
          </FormGroup>
        )}
      </Col>
    </Row>
  )
}

PlantYogurtPackageReview.propTypes = {
  item: PropTypes.object,
  isDisabled: PropTypes.bool,
}

export default PlantYogurtPackageReview
